import { API_BASE } from "@/constants/api";

export type BlogPostSummary = {
  slug: string;
  title: string;
  subtitle?: string | null;
  excerpt?: string | null;
  tags: string[];
  author_name: string;
  published_at: string | null;
  reading_time_minutes: number | null;
  hero_image_url?: string | null;
  og_title?: string | null;
  og_description?: string | null;
  og_image_url?: string | null;
};

export type BlogPostDetail = BlogPostSummary & {
  body_html: string | null;
  body_md: string;
  render_mode: "markdown" | "html";
  locale: string;
  status: string;
};

export type BlogListMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
};

export type BlogListResponse = {
  data: BlogPostSummary[];
  meta: BlogListMeta;
};

function apiUrl(path: string): string {
  return path.startsWith("http") ? path : `${API_BASE}${path}`;
}

export async function fetchBlogPosts(params: {
  page?: number;
  tag?: string;
  perPage?: number;
} = {}): Promise<BlogListResponse> {
  const search = new URLSearchParams();
  search.set("status", "published");
  search.set("page", String(params.page ?? 1));
  search.set("per_page", String(params.perPage ?? 10));
  if (params.tag) {
    search.set("tag", params.tag);
  }

  const response = await fetch(apiUrl(`/api/v1/blog/posts?${search.toString()}`), {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Failed to load posts (${response.status})`);
  }
  return response.json();
}

export async function fetchBlogPost(slug: string): Promise<BlogPostDetail> {
  const response = await fetch(apiUrl(`/api/v1/blog/posts/${encodeURIComponent(slug)}`), {
    headers: { Accept: "application/json" },
  });
  if (response.status === 404) {
    throw Object.assign(new Error("Not found"), { status: 404 });
  }
  if (!response.ok) {
    throw new Error(`Failed to load post (${response.status})`);
  }
  const payload = await response.json();
  return (payload.data ?? payload) as BlogPostDetail;
}
